import { useState } from "react"

import { cn } from "@/lib/utils"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import "dayjs/locale/ja"
import { useIntl } from "react-intl"

dayjs.extend(relativeTime)

export interface TimeAgoProps {
  /** Timestamp as epoch ms, ISO string, or Date */
  date: number | string | Date
  /** Additional class names for the outer wrapper */
  className?: string
  /** Class names applied to the visible text spans */
  textClassName?: string
  /** Class names for the invisible spacer span */
  spacerClassName?: string
  /** Override the locale used for relative time (defaults to current intl locale) */
  locale?: string
  /** Options passed to `intl.formatDate` for the absolute format */
  absoluteFormatOptions?: Intl.DateTimeFormatOptions
}

const DEFAULT_ABSOLUTE_FORMAT: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
}

export function TimeAgo({
  date,
  className,
  textClassName,
  spacerClassName,
  locale: localeProp,
  absoluteFormatOptions = DEFAULT_ABSOLUTE_FORMAT,
}: TimeAgoProps) {
  const intl = useIntl()
  const [hovered, setHovered] = useState(false)

  const resolvedLocale =
    localeProp ??
    (intl.locale?.toLowerCase().startsWith("ja") ? "ja" : "en")

  const relative = dayjs(date).locale(resolvedLocale).fromNow()
  const absolute = intl.formatDate(new Date(date as string | number), absoluteFormatOptions)

  return (
    <span
      className={cn("relative shrink-0 cursor-default text-right", className)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Invisible spacer – always reserves the wider text's width */}
      <span
        className={cn(
          "app-text-label-small invisible whitespace-nowrap",
          spacerClassName
        )}
      >
        {absolute.length > relative.length ? absolute : relative}
      </span>
      {/* Relative (ago) */}
      <span
        className={cn(
          "text-tertiary-text app-text-label-small absolute inset-0 whitespace-nowrap text-right transition-opacity duration-200",
          hovered ? "opacity-0" : "opacity-100",
          textClassName
        )}
      >
        {relative}
      </span>
      {/* Absolute (full datetime) */}
      <span
        className={cn(
          "text-tertiary-text app-text-label-small absolute inset-0 whitespace-nowrap text-right transition-opacity duration-200",
          hovered ? "opacity-100" : "opacity-0",
          textClassName
        )}
      >
        {absolute}
      </span>
    </span>
  )
}
